import React, { useEffect, useState, useCallback } from 'react';
import axiosClient from '../axios-client.js';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import swal from 'sweetalert';

const MEMBERSHIP_TYPES = ['ride_membership', 'train_membership', 'align_membership'];
const isMembership = (type) => MEMBERSHIP_TYPES.includes(type);

const AdminPanel = () => {
  const [reservations, setReservations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userPagination, setUserPagination] = useState(null);

  const [loadingToken, setLoadingToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPack, setLoadingPack] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [paginationPack, setPaginationPack] = useState(null);

  // Packages modal (per user)
  const [packageModal, setPackageModal] = useState(false);
  const [packageModalUser, setPackageModalUser] = useState(null);
  const [userPackages, setUserPackages] = useState([]);
  const [userPackagesPag, setUserPackagesPag] = useState(null);
  const [loadingUserPkgs, setLoadingUserPkgs] = useState(false);
  const [singlePkgId, setSinglePkgId] = useState(null); // set when opened from a reservation

  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = () => {
    axiosClient.get('/perfil').then(({ data }) => {
      if (data.role !== 'staff' && data.role !== 'admin') {
        navigate('/');
      }
      getReservations();
      getPackages();
      getTokens();
      getUsers();
    }).catch(() => navigate('/'));
  };

  // ── Reservations ─────────────────────────────────────────────
  const getReservations = (page = 1) => {
    setLoading(true);
    axiosClient.get('/reservations', { params: { page } })
      .then(({ data }) => {
        setReservations(data.data);
        setPagination(data.pagination);
      })
      .finally(() => setLoading(false));
  };

  // ── Packages ─────────────────────────────────────────────────
  const getPackages = (page = 1) => {
    setLoadingPack(true);
    axiosClient.get('/package-history', { params: { page } })
      .then(({ data }) => {
        setPackages(data.data);
        setPaginationPack(data.pagination);
      })
      .finally(() => setLoadingPack(false));
  };

  // ── Tokens ───────────────────────────────────────────────────
  const getTokens = () => {
    setLoadingToken(true);
    axiosClient.get('/package-tokens')
      .then(({ data }) => setTokens(data.data))
      .finally(() => setLoadingToken(false));
  };

  const valToken = (data) => {
    swal({
      title: 'Validar Token',
      text: `¿Desea validar el token: ${data.token} para el usuario: ${data.user}`,
      icon: 'info',
      buttons: ['Cancelar', 'Confirmar'],
    }).then((result) => {
      if (result) updateToken(data);
    });
  };

  const updateToken = (data) => {
    axiosClient.put('/package-tokens/' + data.id, data)
      .then(() => {
        swal({ title: 'Token validado', text: 'El token se agrego a un paquete.', icon: 'success' })
          .then(() => getTokens());
      })
      .catch((err) => console.log(err));
  };

  // ── Reservations validation ───────────────────────────────────
  const updateReservationValidation = (id, newStatus) => {
    const action = newStatus ? 'marcar como presente' : 'quitar la asistencia';
    swal({
      title: `¿Estás seguro de que deseas ${action}?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Sí'],
      dangerMode: true,
    }).then((willProceed) => {
      if (willProceed) {
        axiosClient.put(`/reservations/${id}/validate`, { validated: newStatus })
          .then(() => swal('¡Asistencia actualizada!', { icon: 'success' }))
          .catch(() => swal('Error al actualizar la asistencia', { icon: 'error' }));
      }
    });
  };

  // ── User packages modal ──────────────────────────────────────
  const openUserPackages = (user) => {
    setSinglePkgId(null);
    setPackageModalUser(user);
    setPackageModal(true);
    loadUserPackages(user.id, 1);
  };

  const openReservationPackage = (reservation) => {
    setSinglePkgId(reservation.package_history_id);
    setPackageModalUser({ name: reservation.user });
    setPackageModal(true);
    setUserPackagesPag(null);
    reloadSinglePackage(reservation.package_history_id);
  };

  const reloadSinglePackage = (pkgId) => {
    setLoadingUserPkgs(true);
    axiosClient.get(`/package-history/${pkgId}`)
      .then(({ data }) => setUserPackages([data.data]))
      .finally(() => setLoadingUserPkgs(false));
  };

  const loadUserPackages = (userId, page) => {
    setLoadingUserPkgs(true);
    axiosClient.get(`/package-history/user/${userId}`, { params: { page } })
      .then(({ data }) => {
        setUserPackages(data.data);
        setUserPackagesPag(data.pagination);
      })
      .finally(() => setLoadingUserPkgs(false));
  };

  const adjustCredit = (pkg, delta) => {
    const newAvailable = pkg.available_classes + delta;
    const newUsed = pkg.used_classes - delta;
    if (newAvailable < 0 || newUsed < 0) {
      swal('Error', 'No hay créditos suficientes para realizar esta operación.', 'error');
      return;
    }
    const action = delta > 0 ? 'reponer 1 crédito' : 'quitar 1 crédito';
    swal({
      title: delta > 0 ? 'Reponer crédito' : 'Quitar crédito',
      text: `¿Deseas ${action} del paquete "${pkg.package}"? (Disponibles: ${pkg.available_classes} → ${newAvailable})`,
      icon: 'warning',
      buttons: ['Cancelar', 'Confirmar'],
    }).then((ok) => {
      if (!ok) return;
      axiosClient.put(`/package-history/${pkg.id}`, {
        total_classes: pkg.total_classes,
        used_classes: newUsed,
        available_classes: newAvailable,
        status: pkg.status,
      }).then(() => {
        swal('¡Listo!', 'Créditos actualizados.', 'success').then(() => {
          if (singlePkgId) {
            reloadSinglePackage(singlePkgId);
          } else {
            loadUserPackages(packageModalUser.id, userPackagesPag?.current_page || 1);
          }
        });
      }).catch(() => swal('Error', 'No se pudo actualizar.', 'error'));
    });
  };

  const releasePenalty = (pkg) => {
    swal({
      title: '¿Liberar penalización?',
      text: '¿Confirmas que recibiste el pago correspondiente?',
      icon: 'warning',
      buttons: ['Cancelar', 'Sí, recibí el pago'],
    }).then((ok) => {
      if (!ok) return;
      axiosClient.put(`/admin/package-history/${pkg.id}/release`)
        .then(() => {
          swal('¡Listo!', 'Penalización liberada. El usuario puede volver a reservar.', 'success').then(() => {
            if (singlePkgId) {
              reloadSinglePackage(singlePkgId);
            } else {
              loadUserPackages(packageModalUser.id, userPackagesPag?.current_page || 1);
            }
            getUsers(userPagination?.current_page || 1, userSearch);
          });
        })
        .catch(() => swal('Error', 'No se pudo liberar la penalización.', 'error'));
    });
  };

  // ── Users ────────────────────────────────────────────────────
  const getUsers = useCallback((page = 1, search = userSearch) => {
    setLoadingUsers(true);
    axiosClient.get('/users', { params: { page, search: search || undefined } })
      .then(({ data }) => {
        setUsers(data.data);
        setUserPagination(data.meta);
      })
      .finally(() => setLoadingUsers(false));
  }, [userSearch]);

  useEffect(() => {
    const timer = setTimeout(() => getUsers(1, userSearch), 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const Pagination = ({ pag, onPage }) => {
    if (!pag) return null;
    return (
      <div className="pagination">
        {pag.prev_page_url && (
          <button onClick={() => onPage(pag.current_page - 1)}>Anterior</button>
        )}
        <span className="me-3 ms-3">{pag.current_page}</span>
        {pag.next_page_url && (
          <button onClick={() => onPage(pag.current_page + 1)}>Siguiente</button>
        )}
      </div>
    );
  };

  return (
    <div className="container">

      {/* ── Reservaciones ─────────────────────────────────────── */}
      {loading ? (
        <div className="loading-container center">
          <div className="spinner-border" role="status" />
        </div>
      ) : (
        <div className="datos-container">
          <p className="semi-bold">Reservaciones</p>
          <hr />
          <div className="table-responsive">
            <Pagination pag={pagination} onPage={getReservations} />
            <table className="table">
              <thead>
                <tr>
                  <th></th>
                  <th>Usuario</th>
                  <th>Paquete</th>
                  <th>Tipo</th>
                  <th>Clase</th>
                  <th>Lugar</th>
                  <th>Hora</th>
                  <th>Estatus</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((data) => (
                  <tr key={data.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={data.validated}
                        onChange={() => updateReservationValidation(data.id, !data.validated)}
                      />
                    </td>
                    <td>{data.user}</td>
                    <td>{data.package}</td>
                    <td>{data.type_class}</td>
                    <td>{data.class_date}</td>
                    <td>{data.bike_number}</td>
                    <td>{data.class_time}</td>
                    <td>{data.status}</td>
                    <td>
                      {data.package_history_id && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openReservationPackage(data)}
                        >
                          Paquete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination pag={pagination} onPage={getReservations} />
          </div>
        </div>
      )}

      {/* ── Paquetes ──────────────────────────────────────────── */}
      {loadingPack ? (
        <div className="loading-container center">
          <div className="spinner-border" role="status" />
        </div>
      ) : (
        <div className="datos-container">
          <p className="semi-bold">Paquetes Comprados</p>
          <hr />
          <div className="table-responsive">
            <Pagination pag={paginationPack} onPage={getPackages} />
            <table className="table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Fecha Compra</th>
                  <th>Fecha Expiración</th>
                  <th>Paquete</th>
                  <th>Compradas</th>
                  <th>Usadas</th>
                  <th>Disponibles</th>
                  <th>Estatus</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((data) => (
                  <tr key={data.id}>
                    <td>{data.user}</td>
                    <td>{data.transaction_date}</td>
                    <td>{data.expiration_date}</td>
                    <td>{data.package}</td>
                    <td>{data.total_classes}</td>
                    <td>{data.used_classes}</td>
                    <td>{data.available_classes}</td>
                    <td>{data.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination pag={paginationPack} onPage={getPackages} />
          </div>
        </div>
      )}

      {/* ── Usuarios ──────────────────────────────────────────── */}
      <div className="datos-container">
        <p className="semi-bold">Usuarios</p>
        <hr />
        <input
          type="text"
          className="form-control mb-3"
          placeholder="Buscar por nombre o correo..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        {loadingUsers ? (
          <div className="loading-container center">
            <div className="spinner-border" role="status" />
          </div>
        ) : (
          <div className="table-responsive">
            <Pagination
              pag={userPagination}
              onPage={(page) => getUsers(page, userSearch)}
            />
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Teléfono</th>
                  <th>Penalizado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.phone}</td>
                    <td>
                      {u.has_penalized_membership && (
                        <span className="badge bg-danger">Penalizado</span>
                      )}
                    </td>
                    <td className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setSelectedUser(u)}
                      >
                        Ver detalle
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openUserPackages(u)}
                      >
                        Paquetes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              pag={userPagination}
              onPage={(page) => getUsers(page, userSearch)}
            />
          </div>
        )}
      </div>

      {/* ── Modal paquetes de usuario ─────────────────────────── */}
      <Modal show={packageModal} onHide={() => setPackageModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Paquetes — {packageModalUser?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingUserPkgs ? (
            <div className="text-center py-3">
              <div className="spinner-border" role="status" />
            </div>
          ) : userPackages.length === 0 ? (
            <p className="text-muted text-center">Este usuario no tiene paquetes.</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Paquete</th>
                      <th>Compra</th>
                      <th>Expiración</th>
                      <th>Total</th>
                      <th>Usadas</th>
                      <th>Disponibles</th>
                      <th>Estatus</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPackages.map((pkg) => (
                      <tr key={pkg.id}>
                        <td>
                          {pkg.package}
                          {pkg.penalized && (
                            <span className="badge bg-danger ms-2">Penalizado</span>
                          )}
                        </td>
                        <td>{pkg.transaction_date}</td>
                        <td>{pkg.expiration_date}</td>
                        {isMembership(pkg.package_type) ? (
                          <td colSpan={3} className="text-center fst-italic text-muted" style={{ fontSize: '0.82rem' }}>
                            Acceso ilimitado
                          </td>
                        ) : (
                          <>
                            <td>{pkg.total_classes}</td>
                            <td>{pkg.used_classes}</td>
                            <td><strong>{pkg.available_classes}</strong></td>
                          </>
                        )}
                        <td>{pkg.status}</td>
                        <td className="d-flex gap-1">
                          {isMembership(pkg.package_type) ? (
                            pkg.penalized && (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => releasePenalty(pkg)}
                              >
                                Liberar
                              </button>
                            )
                          ) : (
                            <>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Quitar 1 crédito"
                                onClick={() => adjustCredit(pkg, -1)}
                              >
                                −1
                              </button>
                              <button
                                className="btn btn-sm btn-outline-success"
                                title="Reponer 1 crédito"
                                onClick={() => adjustCredit(pkg, +1)}
                              >
                                +1
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {userPackagesPag && (userPackagesPag.prev_page_url || userPackagesPag.next_page_url) && (
                <div className="pagination mt-2">
                  {userPackagesPag.prev_page_url && (
                    <button onClick={() => loadUserPackages(packageModalUser.id, userPackagesPag.current_page - 1)}>
                      Anterior
                    </button>
                  )}
                  <span className="ms-3 me-3">{userPackagesPag.current_page} / {userPackagesPag.last_page}</span>
                  {userPackagesPag.next_page_url && (
                    <button onClick={() => loadUserPackages(packageModalUser.id, userPackagesPag.current_page + 1)}>
                      Siguiente
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPackageModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal detalle usuario ─────────────────────────────── */}
      <Modal show={!!selectedUser} onHide={() => setSelectedUser(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Detalle de usuario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <table className="table table-borderless mb-0">
              <tbody>
                <tr><td className="fw-semibold">ID</td><td>{selectedUser.id}</td></tr>
                <tr><td className="fw-semibold">Nombre</td><td>{selectedUser.name}</td></tr>
                <tr><td className="fw-semibold">Correo</td><td>{selectedUser.email}</td></tr>
                <tr><td className="fw-semibold">Teléfono</td><td>{selectedUser.phone || '—'}</td></tr>
                <tr><td className="fw-semibold">Rol</td><td>{selectedUser.role}</td></tr>
                <tr><td className="fw-semibold">Fecha de nacimiento</td><td>{selectedUser.birth_date || '—'}</td></tr>
                <tr><td className="fw-semibold">Contacto de emergencia</td><td>{selectedUser.emergency_contact_name || '—'}</td></tr>
                <tr><td className="fw-semibold">Tel. emergencia</td><td>{selectedUser.emergency_phone || '—'}</td></tr>
                <tr><td className="fw-semibold">Registro</td><td>{selectedUser.created_at}</td></tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedUser(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminPanel;